import 'bootstrap/dist/css/bootstrap.css';
import { useEffect, useState } from 'react';
import { Button } from 'react-bootstrap';
import { useLocation } from 'react-router-dom';
import Spinner from '../../../shared/spinner';
import { OperacaoDTO } from '../../../shared/dto/operacaoDTO';
import { format, parse, parseISO } from 'date-fns';
import ScrollToTopButton from '../../../shared/scrollToTopButton';
import { OperacaoParcela } from '../../../shared/model/operacaoParcela';
import OperacaoParcelaService from '../../../services/operacaoParcelaService';
import OperacaoService from '../../../services/operacaoService';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalculator } from '@fortawesome/free-solid-svg-icons';
import { BaixaParcelaDTO } from '../../../shared/dto/baixaParcelaDTO';
import { Originador } from '../../../shared/model/originador';
import { OperacaoCompraDivida } from '../../../shared/model/operacaoCompraDivida';
import { TipoPagamentoEnum } from '../../../shared/constantes/tipoPagamentoEnum';
import { Banco } from '../../../shared/model/banco';
import BancoService from '../../../services/bancoService';
import { calcularTotaisParcelasQuitacao, getParcelaQuitacaoKey } from '../../../shared/parcelasQuitacao';
import DateInput from '../../../shared/dateInput';
import RemessaCessaoService from '../../../services/remessaCessaoService';
import { CessaoOperacaoDetalheDTO } from '../../../shared/model/CessaoOperacaoDetalheDTO';


// Função auxiliar para formatação de valores monetários
const formatarMoeda = (valor: number | null | undefined): string => {
  if (valor == null || isNaN(valor)) {
    return 'R$ 0,00';
  }
  return 'R$ ' + valor.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

// Função auxiliar para formatação de taxas
const formatarTaxa = (valor: number | null | undefined, casasDecimais: number = 4): string => {
  if (valor == null || isNaN(valor)) {
    return '0,0000';
  }
  return valor.toLocaleString('pt-BR', { minimumFractionDigits: casasDecimais, maximumFractionDigits: casasDecimais });
};

const isValorNaoInformado = (valor: string | number | null | undefined): boolean => {
  return valor == null || valor === '' || valor === 'N/A';
};

const formatarTextoDetalheParcela = (valor: string | number | null | undefined): string => {
  return isValorNaoInformado(valor) ? '' : String(valor);
};

const formatarTipoDetalheParcela = (tipoMovimentacao: string | null | undefined): string => {
  if (isValorNaoInformado(tipoMovimentacao)) {
    return 'EM ABERTO';
  }

  return tipoMovimentacao!.replace(/_/g, ' ');
};

const formatarDataDetalheParcela = (data: Date | string | null | undefined): string => {
  if (data == null || data === '' || data === 'N/A') {
    return '';
  }

  const dataFormatada = new Date(data);
  return isNaN(dataFormatada.getTime()) ? '' : format(dataFormatada, 'dd/MM/yyyy');
};

const formatarStatusParcela = (tipoMovimentacao: string | null | undefined): string => {
  if (!tipoMovimentacao) {
    return 'PENDENTE';
  }

  if (tipoMovimentacao === 'EM_ABERTO') {
    return 'EM ABERTO';
  }

  if (tipoMovimentacao === 'BAIXA_PARCIAL') {
    return 'BAIXA PARCIAL';
  }

  if (tipoMovimentacao.startsWith('BAIXA')) {
    return 'BAIXA TOTAL';
  }

  return tipoMovimentacao.replace(/_/g, ' ');
};

const isBaixaTotalParcela = (tipoMovimentacao: string | null | undefined): boolean => {
  return !!tipoMovimentacao
    && tipoMovimentacao.startsWith('BAIXA')
    && tipoMovimentacao !== 'BAIXA_PARCIAL';
};

const isEstornoParcela = (tipoMovimentacao: string | null | undefined): boolean => {
  return !!tipoMovimentacao
    && (tipoMovimentacao.includes('ESTORNO') || tipoMovimentacao.includes('EXTORNO'));
};

const isMovimentacaoSemSaldoAtual = (tipoMovimentacao: string | null | undefined): boolean => {
  return !!tipoMovimentacao
    && (tipoMovimentacao.includes('ALTERACAO_DE_VENCIMENTO')
      || tipoMovimentacao.includes('REPACTUADA')
      || isEstornoParcela(tipoMovimentacao));
};

const calcularDescontoParcela = (parcela: OperacaoParcela): number => {
  if (parcela.ativo === false) {
    return 0;
  }

  if (!isBaixaTotalParcela(parcela.tipoMovimentacao)) {
    return 0;
  }

  return Math.max(0, (parcela.valorParcela || 0) - (parcela.valorPago || 0));
};

const calcularSaldoRemanescenteParcela = (parcela: OperacaoParcela, isLinhaAtual: boolean): number => {
  if (parcela.ativo === false
    || !isLinhaAtual
    || isBaixaTotalParcela(parcela.tipoMovimentacao)
    || isMovimentacaoSemSaldoAtual(parcela.tipoMovimentacao)) {
    return 0;
  }

  return Math.max(0, (parcela.valorParcela || 0) - (parcela.valorPago || 0));
};

const formatarValorPagoParcela = (parcela: OperacaoParcela): string => {
  if (isEstornoParcela(parcela.tipoMovimentacao)) {
    return formatarMoeda(-Math.abs(parcela.valorParcela || parcela.valorPago || 0));
  }

  if (!parcela.tipoMovimentacao || parcela.tipoMovimentacao.startsWith('BAIXA')) {
    return formatarMoeda(parcela.valorPago);
  }

  return '-';
};

const formatarNaturezaBaixaParcela = (parcela: OperacaoParcela): string => {
  const tipoMovimentacao = parcela.tipoMovimentacao || '';

  if ((!parcela.isFinanceira && parcela.dtMovimentacao == null)
    || isMovimentacaoSemSaldoAtual(tipoMovimentacao)) {
    return 'N/A';
  }

  return !parcela.isFinanceira && parcela.dtMovimentacao != null
    ? 'Baixa Não Financeira'
    : 'Baixa Financeira';
};

// Função auxiliar para formatação de datas
const formatarData = (data: Date | string | null | undefined): string => {
  if (data == null) {
    return 'Não Disponível';
  }
  // Tenta parsear se for string (caso venha da API) ou usa como Date
  const dateValue = typeof data === 'string' ? parseISO(data) : data;

  if (dateValue instanceof Date && !isNaN(dateValue.getTime())) {
    return format(dateValue, 'dd/MM/yyyy');
  }
  return 'Não Disponível';
};


function DetalheContratoComponent() {
  const location = useLocation();
  const { contrato } = location.state || {};
  const [botaoPagina, setBotaoPagina] = useState(1);
  const [loading, setLoading] = useState(false);

  // Inicializando com um objeto vazio para garantir que as propriedades existam, se for o caso
  // Dependendo de como a OperacaoDTO é definida, pode ser melhor manter como undefined/null
  // Aqui, mantemos como undefined para forçar o tratamento condicional, que é mais seguro
  const [contratoDetalhe, setContratoDetalhe] = useState<OperacaoDTO>();
  const [parcelasContrato, setParcelasContrato] = useState<OperacaoParcela[]>([]);
  const [cessaoOperacao, setCessaoOperacao] = useState<CessaoOperacaoDetalheDTO>();
  const [contratosComprados, setContratosComprados] = useState<OperacaoCompraDivida[]>([]);

  const [exibeDetalhe, setExibeDetalhe] = useState(false);
  const [dtMovimento, setDtMovimento] = useState(format(new Date(), 'dd/MM/yyyy'));
  const [baixaDto, setBaixaDto] = useState<BaixaParcelaDTO>();
  const [parcelasList, setParcelasList] = useState<OperacaoParcela[] | null>([]);
  const [parcelasSelecionadas, setParcelasSelecionadas] = useState<string[]>([]);

  const operacaoParcelaService: OperacaoParcelaService = new OperacaoParcelaService();
  const operacaoService: OperacaoService = new OperacaoService();
  const remessaCessaoService: RemessaCessaoService = new RemessaCessaoService();
  const bancoService: BancoService = new BancoService();
  const [bancos, setBancos] = useState<Banco[]>([]);

  const parcelasSelecionadasList = (parcelasList ?? []).filter((item) =>
    parcelasSelecionadas.includes(getParcelaQuitacaoKey(item))
  );
  const totaisParcelasSelecionadas = calcularTotaisParcelasQuitacao(parcelasSelecionadasList);
  const todasParcelasSelecionadas = parcelasList != null
    && parcelasList.length > 0
    && parcelasSelecionadasList.length === parcelasList.length;
  const ultimaLinhaPorParcela = parcelasContrato.reduce<Record<string, number>>((ultimas, parcela, index) => {
    const chaveParcela = String(parcela.numeroParcela ?? index);
    const ultimaAtual = ultimas[chaveParcela];
    const idUltimaAtual = ultimaAtual != null ? parcelasContrato[ultimaAtual]?.id ?? 0 : 0;
    const idParcela = parcela.id ?? 0;

    if (ultimaAtual == null || idParcela >= idUltimaAtual) {
      ultimas[chaveParcela] = index;
    }
    return ultimas;
  }, {});

  const selecionarTodasParcelas = (marcar: boolean) => {
    setParcelasSelecionadas(marcar && parcelasList ? parcelasList.map(getParcelaQuitacaoKey) : []);
  };

  const selecionarParcela = (parcela: OperacaoParcela, marcar: boolean) => {
    const key = getParcelaQuitacaoKey(parcela);

    setParcelasSelecionadas((selecionadas) => {
      if (marcar) {
        return selecionadas.includes(key) ? selecionadas : [...selecionadas, key];
      }

      return selecionadas.filter((item) => item !== key);
    });
  };

  const listarContrato = async () => {
    try {
      setLoading(true);
      const responseList = await operacaoService.listarContratoDetalhePorContrato(contrato);
      const data = responseList.data;

      setContratoDetalhe(data);
      if (data?.proposta) {
        const responseList2 = await operacaoService.listarContratoCompraDividaPorContrato(data.proposta);
        if (responseList2.data) {
          const data2 = responseList2.data;
          setContratosComprados(data2);
        }
      }
      setLoading(false);
    } catch (err) {
      setLoading(false);
      console.error(err);
    }
  };

  const listarParcelasContrato = async () => {
    try {
      setLoading(true);
      const responseList = await operacaoParcelaService.listarParcelasPorContrato(contrato);
      const json = responseList.data;
      setParcelasContrato(json);
      setLoading(false);
    } catch (err) {
      setLoading(false);
      console.error(err);
    }
  };

  const listarCessaoContrato = async () => {
    try {
      if (!contratoDetalhe?.id) return;

      setLoading(true);
      const responseList = await remessaCessaoService.listarCessaoPorIdOperacao(contratoDetalhe.id);
      const json = responseList.data;

      // Se retornar uma lista de DTOs, pegamos o registro ativo (primeiro elemento)
      if (Array.isArray(json) && json.length > 0) {
        setCessaoOperacao(json[0]);
      } else {
        setCessaoOperacao(json);
      }
      setLoading(false);
    } catch (err) {
      setLoading(false);
      console.error('Erro ao carregar cessão:', err);
    }
  };

  const listarParcelasDesconto = async () => {
    try {
      setParcelasList([]);
      setLoading(true);
      let dto: BaixaParcelaDTO = new BaixaParcelaDTO();
      dto.dtMovimento = parse(dtMovimento, "dd/MM/yyyy", new Date());

      const responseList = await operacaoParcelaService.listarParcelasParaBaixa(contrato, dto);
      const json = responseList.data;
      const parcelasCalculadas = json.parcelas || [];
      setBaixaDto(json);
      setParcelasList(parcelasCalculadas);
      setParcelasSelecionadas(parcelasCalculadas.map(getParcelaQuitacaoKey));
      setExibeDetalhe(true);

      setLoading(false);
    } catch (err) {
      setLoading(false);
      console.error(err);
    }
  }

  const listarTodosBancos = async () => {
    try {
      setLoading(true);
      const responseList = await bancoService.listarTodosBancos();
      setBancos(responseList.data);
      setLoading(false);
    } catch (err) {
      setLoading(false);
      console.error(err);
    }
  };

  useEffect(() => {
    listarContrato();
    listarTodosBancos();

  }, []);

  return (
    <>
      <div className="position-relative m-md-3">
        <div style={{ marginBottom: '15px', display: 'flex', justifyContent: 'space-between' }}>
          <h5>Detalhes do Contrato: {contrato || 'Não Informado'}</h5>
          <Button id="App-button-cor-principal"
            onClick={() => window.history.back()}
          > Voltar
          </Button>
        </div>
        {loading && !contratoDetalhe ? <Spinner loading={loading} /> : (
          <>
            <div>
              <Button style={{ backgroundColor: '#6c757d', borderColor: '#6c757d', color: 'white', width: '10%', height: '50px', marginLeft: '10px' }}
                onClick={() => setBotaoPagina(1)}>
                Contrato
              </Button>
              <Button style={{ backgroundColor: '#6c757d', borderColor: '#6c757d', color: 'white', width: '10%', height: '50px', marginLeft: '10px' }}
                onClick={() => { setBotaoPagina(2); listarParcelasContrato(); }}>
                Parcelas
              </Button>
              <Button style={{ backgroundColor: '#6c757d', borderColor: '#6c757d', color: 'white', width: '15%', height: '50px', marginLeft: '10px' }}
                onClick={() => setBotaoPagina(3)}>
                Saldo Devedor
              </Button>
              <Button
                style={{ backgroundColor: '#6c757d', borderColor: '#6c757d', color: 'white', width: '15%', height: '50px', marginLeft: '10px' }}
                onClick={() => {
                  listarCessaoContrato();
                  setBotaoPagina(4);
                }}
              >
                Cessão
              </Button>
            </div>

            {/* ABA 1: CONTRATO */}
            {botaoPagina === 1 && contratoDetalhe ? (
              <>
                <div className="card" style={{ marginTop: '25px' }}>
                  <div className="card-body">
                    <div className="card-header">
                      <strong>Origem</strong>
                    </div>
                    <div className="row">
                      <div className="col-md-3">
                        <div className="form-group">
                          <span style={{ textAlign: 'left' }}>Matriz</span>
                          <input type="text" value={contratoDetalhe.matriz || 'Não Disponível'} className="form-control form-control-sm brds " style={{ color: 'Black', backgroundColor: 'LightGrey' }} readOnly />
                        </div>
                      </div>
                      <div className="col-md-3">
                        <div className="form-group">
                          <span style={{ textAlign: 'left' }}>Promotora</span>
                          <input type="text" value={contratoDetalhe.promotora || 'Não Disponível'} className="form-control form-control-sm brds " style={{ color: 'Black', backgroundColor: 'LightGrey' }} readOnly />
                        </div>
                      </div>
                      <div className="col-md-3">
                        <div className="form-group">
                          <span style={{ textAlign: 'left' }}>Supervisor</span>
                          <input type="text" value={contratoDetalhe.supervisor || 'Não Disponível'} className="form-control form-control-sm brds " style={{ color: 'Black', backgroundColor: 'LightGrey' }} readOnly />
                        </div>
                      </div>
                    </div>
                    <div className="row">
                      <div className="col-md-3">
                        <div className="form-group">
                          <span style={{ textAlign: 'left' }}>Operador</span>
                          <input type="text" value={contratoDetalhe.operador || 'Não Disponível'} className="form-control form-control-sm brds " style={{ color: 'Black', backgroundColor: 'LightGrey' }} readOnly />
                        </div>
                      </div>
                      <div className="col-md-3">
                        <div className="form-group">
                          <span style={{ textAlign: 'left' }}>Ponto de Venda</span>
                          <input type="text" value={contratoDetalhe.pontoVenda || 'Não Disponível'} className="form-control form-control-sm brds " style={{ color: 'Black', backgroundColor: 'LightGrey' }} readOnly />
                        </div>
                      </div>

                      <div className="col-md-3">
                        <div className="form-group">
                          <span style={{ textAlign: 'left' }}>{Originador.DESCRITIVO_GERAL}</span>
                          <input type="text" value={contratoDetalhe.originador || 'Não Disponível'} className="form-control form-control-sm brds " style={{ color: 'Black', backgroundColor: 'LightGrey' }} readOnly />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="card-body">
                    <div className="card-header">
                      <strong>Operação</strong>
                    </div>
                    <div className="row">
                      <div className="col-md-3">
                        <div className="form-group">
                          <span style={{ textAlign: 'left' }}>Contrato Externo</span>
                          <input
                            type="text"
                            value={contratoDetalhe.numeroContratoExterno || 'Não Disponível'}
                            className="form-control form-control-sm brds"
                            style={{ color: 'Black', backgroundColor: 'LightGrey' }}
                            readOnly
                          />
                        </div>
                      </div>
                      <div className="col-md-3">
                        <div className="form-group">
                          <span style={{ textAlign: 'left' }}>Produto</span>
                          <input type="text" value={contratoDetalhe.produto || 'Não Disponível'} className="form-control form-control-sm brds " style={{ color: 'Black', backgroundColor: 'LightGrey' }} readOnly />
                        </div>
                      </div>
                    </div>
                    <div className="row">
                      <div className="col-md-3">
                        <div className="form-group">
                          <span style={{ textAlign: 'left' }}>Tabela de Financiamento</span>
                          <input type="text" value={contratoDetalhe.tabelaFinanciamento || 'Não Disponível'} className="form-control form-control-sm brds " style={{ color: 'Black', backgroundColor: 'LightGrey' }} readOnly />
                        </div>
                      </div>
                      <div className="col-md-3">
                        <div className="form-group">
                          <span style={{ textAlign: 'left' }}>Empregador</span>
                          <input type="text" value={contratoDetalhe.empregador || 'Não Disponível'} className="form-control form-control-sm brds " style={{ color: 'Black', backgroundColor: 'LightGrey' }} readOnly />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="card-body">
                    <div className="card-header">
                      <strong>Cliente</strong>
                    </div>
                    <div className="row">
                      <div className="col-md-4">
                        <div className="form-group">
                          <span style={{ textAlign: 'left' }}>Endereço</span>
                          <input type="text" value={contratoDetalhe.logradouroEndereco || 'Não Disponível'} className="form-control form-control-sm brds " style={{ color: 'Black', backgroundColor: 'LightGrey' }} readOnly />
                        </div>
                      </div>
                      <div className="col-md-2">
                        <div className="form-group">
                          <span style={{ textAlign: 'left' }}>Número</span>
                          <input type="text" value={contratoDetalhe.numeroEndereco || 'Não Disponível'} className="form-control form-control-sm brds " style={{ color: 'Black', backgroundColor: 'LightGrey' }} readOnly />
                        </div>
                      </div>
                      <div className="col-md-3">
                        <div className="form-group">
                          <span style={{ textAlign: 'left' }}>Complemento</span>
                          <input type="text" value={contratoDetalhe.complementoEndereco || 'Não Disponível'} className="form-control form-control-sm brds " style={{ color: 'Black', backgroundColor: 'LightGrey' }} readOnly />
                        </div>
                      </div>
                      <div className="col-md-3">
                        <div className="form-group">
                          <span style={{ textAlign: 'left' }}>Bairro</span>
                          <input type="text" value={contratoDetalhe.bairroEndereco || 'Não Disponível'} className="form-control form-control-sm brds " style={{ color: 'Black', backgroundColor: 'LightGrey' }} readOnly />
                        </div>
                      </div>
                    </div>
                    <div className="row">
                      <div className="col-md-3">
                        <div className="form-group">
                          <span style={{ textAlign: 'left' }}>Cidade</span>
                          <input type="text" value={contratoDetalhe.localidadeEndereco || 'Não Disponível'} className="form-control form-control-sm brds " style={{ color: 'Black', backgroundColor: 'LightGrey' }} readOnly />
                        </div>
                      </div>
                      <div className="col-md-2">
                        <div className="form-group">
                          <span style={{ textAlign: 'left' }}>UF</span>
                          <input type="text" value={contratoDetalhe.ufEndereco || 'Não Disponível'} className="form-control form-control-sm brds " style={{ color: 'Black', backgroundColor: 'LightGrey' }} readOnly />
                        </div>
                      </div>
                      <div className="col-md-3">
                        <div className="form-group">
                          <span style={{ textAlign: 'left' }}>CEP</span>
                          <input type="text" value={contratoDetalhe.cepEndereco || 'Não Disponível'} className="form-control form-control-sm brds " style={{ color: 'Black', backgroundColor: 'LightGrey' }} readOnly />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="card-body">
                    <div className="card-header">
                      <strong>Valores</strong>
                    </div>
                    <div className="row">
                      <div className="col-md-2">
                        <div className="form-group">
                          <span style={{ textAlign: 'left' }}>Valor Solicitado</span>
                          <input type="text" value={formatarMoeda(contratoDetalhe.valorFinanciado)} className="form-control form-control-sm brds " style={{ color: 'Black', backgroundColor: 'LightGrey', textAlign: 'right' }} readOnly />
                        </div>
                      </div>
                      <div className="col-md-2">
                        <div className="form-group">
                          <span style={{ textAlign: 'left' }}>Valor IOF</span>
                          <input type="text" value={formatarMoeda(contratoDetalhe.valorIof)} className="form-control form-control-sm brds " style={{ color: 'Black', backgroundColor: 'LightGrey', textAlign: 'right' }} readOnly />
                        </div>
                      </div>
                      <div className="col-md-2">
                        <div className="form-group">
                          <span style={{ textAlign: 'left' }}>Valor Principal</span>
                          <input type="text" value={formatarMoeda(contratoDetalhe.valorFinanciado)} className="form-control form-control-sm brds " style={{ color: 'Black', backgroundColor: 'LightGrey', textAlign: 'right' }} readOnly />
                        </div>
                      </div>
                      <div className="col-md-2">
                        <div className="form-group">
                          <span style={{ textAlign: 'left' }}>Valor Parcela</span>
                          <input type="text" value={formatarMoeda(contratoDetalhe.valorParcela)} className="form-control form-control-sm brds " style={{ color: 'Black', backgroundColor: 'LightGrey', textAlign: 'right' }} readOnly />
                        </div>
                      </div>
                    </div>
                    <div className="row">
                      <div className="col-md-2">
                        <div className="form-group">
                          <span style={{ textAlign: 'left' }}>Valor Líquido</span>
                          <input type="text" value={formatarMoeda(contratoDetalhe.valorLiquido)} className="form-control form-control-sm brds " style={{ color: 'Black', backgroundColor: 'LightGrey', textAlign: 'right' }} readOnly />
                        </div>
                      </div>
                      <div className="col-md-2">
                        <div className="form-group">
                          <span style={{ textAlign: 'left' }}>Valor Bruto</span>
                          <input type="text" value={formatarMoeda(contratoDetalhe.valorBruto)} className="form-control form-control-sm brds " style={{ color: 'Black', backgroundColor: 'LightGrey', textAlign: 'right' }} readOnly />
                        </div>
                      </div>
                      <div className="col-md-2">
                        <div className="form-group">
                          <span style={{ textAlign: 'left' }}>Valor Transf</span>
                          <input type="text" value={'R$ 0,00'} className="form-control form-control-sm brds " style={{ color: 'Black', backgroundColor: 'LightGrey', textAlign: 'right' }} readOnly />
                        </div>
                      </div>
                      <div className="col-md-2">
                        <div className="form-group">
                          <span style={{ textAlign: 'left' }}>Valor Refin</span>
                          <input type="text" value={formatarMoeda(contratoDetalhe.valorRefin)} className="form-control form-control-sm brds " style={{ color: 'Black', backgroundColor: 'LightGrey', textAlign: 'right' }} readOnly />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="card-body">
                    <div className="card-header">
                      <strong>Taxas</strong>
                    </div>
                    <div className="row">
                      <div className="col-md-2">
                        <div className="form-group">
                          <span style={{ textAlign: 'left' }}>Taxa CL a.m.</span>
                          <input type="text" value={formatarTaxa(contratoDetalhe.taxaClAm)} className="form-control form-control-sm brds" readOnly
                            style={{ color: 'Black', backgroundColor: 'LightGrey', textAlign: 'right' }} />
                        </div>
                      </div>
                      <div className="col-md-2">
                        <div className="form-group">
                          <span style={{ textAlign: 'left' }}>Taxa AP a.m.</span>
                          <input type="text" value={formatarTaxa(contratoDetalhe.taxaApAm)} className="form-control form-control-sm brds" readOnly
                            style={{ color: 'Black', backgroundColor: 'LightGrey', textAlign: 'right' }} />
                        </div>
                      </div>
                      <div className="col-md-2">
                        <div className="form-group">
                          <span style={{ textAlign: 'left' }}>Taxa CET a.m.</span>
                          <input type="text" value={formatarTaxa(contratoDetalhe.taxaCetAm)} className="form-control form-control-sm brds" readOnly
                            style={{ color: 'Black', backgroundColor: 'LightGrey', textAlign: 'right' }} />
                        </div>
                      </div>
                      <div className="col-md-2">
                        <div className="form-group">
                          <span style={{ textAlign: 'left' }}>Taxa Nominal a.m.</span>
                          <input type="text" value={formatarTaxa(contratoDetalhe.taxaNominalAm)} className="form-control form-control-sm brds" readOnly
                            style={{ color: 'Black', backgroundColor: 'LightGrey', textAlign: 'right' }} />
                        </div>
                      </div>
                    </div>
                    <div className="row">
                      <div className="col-md-2">
                        <div className="form-group">
                          <span style={{ textAlign: 'left' }}>Taxa CL a.a.</span>
                          <input type="text" value={formatarTaxa(contratoDetalhe.taxaClAa)} className="form-control form-control-sm brds" readOnly
                            style={{ color: 'Black', backgroundColor: 'LightGrey', textAlign: 'right' }} />
                        </div>
                      </div>
                      <div className="col-md-2">
                        <div className="form-group">
                          <span style={{ textAlign: 'left' }}>Taxa AP a.a.</span>
                          <input
                            type="text" value={formatarTaxa(contratoDetalhe.taxaApAa)} className="form-control form-control-sm brds" readOnly
                            style={{ color: 'Black', backgroundColor: 'LightGrey', textAlign: 'right' }} />
                        </div>
                      </div>
                      <div className="col-md-2">
                        <div className="form-group">
                          <span style={{ textAlign: 'left' }}>Taxa CET a.a.</span>
                          <input type="text" value={formatarTaxa(contratoDetalhe.taxaCetAa)} className="form-control form-control-sm brds" readOnly
                            style={{ color: 'Black', backgroundColor: 'LightGrey', textAlign: 'right' }} />
                        </div>
                      </div>
                      <div className="col-md-2">
                        <div className="form-group">
                          <span style={{ textAlign: 'left' }}>Taxa Nominal a.a.</span>
                          <input type="text" value={formatarTaxa(contratoDetalhe.taxaNominalAa)} className="form-control form-control-sm brds" readOnly
                            style={{ color: 'Black', backgroundColor: 'LightGrey', textAlign: 'right' }} />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="card-body">
                    <div className="card-header">
                      <strong>Prazos</strong>
                    </div>
                    <div className="row">
                      <div className="col-md-2">
                        <div className="form-group">
                          <span style={{ textAlign: 'left' }}>Quantidade Parcelas</span>
                          <input type="text" value={contratoDetalhe.prazo != null ? contratoDetalhe.prazo.toString() : '0'} className="form-control form-control-sm brds" readOnly
                            style={{ color: 'Black', backgroundColor: 'LightGrey', textAlign: 'right' }} />
                        </div>
                      </div>
                      <div className="col-md-2">
                        <div className="form-group">
                          <span style={{ textAlign: 'left' }}>Data Base</span>
                          <input type="text" className="form-control form-control-sm brds" readOnly
                            value={formatarData(contratoDetalhe.dtBase)}
                            style={{ color: 'Black', backgroundColor: 'LightGrey', textAlign: 'right' }} />
                        </div>
                      </div>
                      <div className="col-md-2">
                        <div className="form-group">
                          <span style={{ textAlign: 'left' }}>Primeiro Vencimento</span>
                          <input type="text" className="form-control form-control-sm brds" readOnly
                            value={formatarData(contratoDetalhe.dtPrimeiroVencimento)}
                            style={{ color: 'Black', backgroundColor: 'LightGrey', textAlign: 'left' }} />
                        </div>
                      </div>
                      <div className="col-md-2">
                        <div className="form-group">
                          <span style={{ textAlign: 'left' }}>Último Vencimento</span>
                          <input type="text" className="form-control form-control-sm brds" readOnly
                            value={formatarData(contratoDetalhe.dtUltimoVencimento)}
                            style={{ color: 'Black', backgroundColor: 'LightGrey', textAlign: 'left' }} />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="card-body">
                    <div className="card-header">
                      <strong>LIBERAÇÕES</strong>
                    </div>
                    <div className="row">
                      <div className="col-md-1">
                        <div className="form-group">
                          <span style={{ textAlign: 'center' }}>NÚMERO</span>
                          <input type="text" value={'1'} className="form-control form-control-sm brds" readOnly
                            style={{ color: 'Black', backgroundColor: 'LightGrey', textAlign: 'center' }} />
                        </div>
                      </div>
                      <div className="col-md-2">
                        <div className="form-group">
                          <span style={{ textAlign: 'center' }}>CPF</span>
                          <input type="text" value={contratoDetalhe.cpfCliente || 'Não Disponível'} className="form-control form-control-sm brds" readOnly
                            style={{ color: 'Black', backgroundColor: 'LightGrey', textAlign: 'center' }} />
                        </div>
                      </div>
                      <div className="col-md-3">
                        <div className="form-group">
                          <span style={{ textAlign: 'center' }}>NOME</span>
                          <input type="text" value={contratoDetalhe.nomeCliente || 'Não Disponível'} className="form-control form-control-sm brds" readOnly
                            style={{ color: 'Black', backgroundColor: 'LightGrey', textAlign: 'center' }} />
                        </div>
                      </div>
                      <div className="col-md-1">
                        <div className="form-group">
                          <span style={{ textAlign: 'center' }}>BANCO</span>
                          <input type="text" value={contratoDetalhe.banco || 'N/A'} className="form-control form-control-sm brds" readOnly
                            style={{ color: 'Black', backgroundColor: 'LightGrey', textAlign: 'center' }} />
                        </div>
                      </div>
                      <div className="col-md-1">
                        <div className="form-group">
                          <span style={{ textAlign: 'center' }}>AGÊNCIA</span>
                          <input type="text" value={contratoDetalhe.agencia || 'N/A'} className="form-control form-control-sm brds" readOnly
                            style={{ color: 'Black', backgroundColor: 'LightGrey', textAlign: 'center' }} />
                        </div>
                      </div>
                      <div className="col-md-1">
                        <div className="form-group">
                          <span style={{ textAlign: 'center' }}>CONTA</span>
                          <input type="text" value={contratoDetalhe.conta || 'N/A'} className="form-control form-control-sm brds" readOnly
                            style={{ color: 'Black', backgroundColor: 'LightGrey', textAlign: 'center' }} />
                        </div>
                      </div>
                      <div className="col-md-1">
                        <div className="form-group">
                          <span style={{ textAlign: 'center' }}>DV</span>
                          <input type="text" value={contratoDetalhe.dvConta || 'N/A'} className="form-control form-control-sm brds" readOnly
                            style={{ color: 'Black', backgroundColor: 'LightGrey', textAlign: 'center' }} />
                        </div>
                      </div>
                      <div className="col-md-1">
                        <div className="form-group">
                          <span style={{ textAlign: 'center' }}>VALOR</span>
                          <input type="text" value={formatarMoeda(contratoDetalhe.valorLiquido)} className="form-control form-control-sm brds" readOnly
                            style={{ color: 'Black', backgroundColor: 'LightGrey', textAlign: 'center' }} />
                        </div>
                      </div>
                    </div>

                    {contratosComprados?.map((item, index) => (
                      <div className="row" key={index} style={{ marginTop: '10px' }}>
                        <div className="col-md-1">
                          <div className="form-group">
                            <span style={{ textAlign: 'center' }}>NÚMERO</span>
                            <input type="text" value={index + 2} className="form-control form-control-sm brds" readOnly
                              style={{ color: 'Black', backgroundColor: 'LightGrey', textAlign: 'center' }} />
                          </div>
                        </div>
                        <div className="col-md-2">
                          <div className="form-group">
                            <span style={{ textAlign: 'center' }}>BANCO / ENTIDADE</span>
                            <input type="text" value={bancos.find((banco) => banco.id === item?.idBanco!)?.nome} className="form-control form-control-sm brds" readOnly
                              style={{ color: 'Black', backgroundColor: 'LightGrey', textAlign: 'center' }} />
                          </div>
                        </div>
                        <div className="col-md-1">
                          <div className="form-group">
                            <span style={{ textAlign: 'center' }}>CONTRATO</span>
                            <input type="text" value={item.numeroContrato || ''} className="form-control form-control-sm brds" readOnly
                              style={{ color: 'Black', backgroundColor: 'LightGrey', textAlign: 'center' }} />
                          </div>
                        </div>
                        {item.tipoPagamento === 4 ? (
                          <>
                            <div className="col-md-1">
                              <div className="form-group">
                                <span style={{ textAlign: 'center' }}>BANCO</span>
                                <input type="text" value={item.codigoBanco || ''} className="form-control form-control-sm brds" readOnly
                                  style={{ color: 'Black', backgroundColor: 'LightGrey', textAlign: 'center' }} />
                              </div>
                            </div>
                            <div className="col-md-1">
                              <div className="form-group">
                                <span style={{ textAlign: 'center' }}>AGÊNCIA</span>
                                <input type="text" value={item.agencia || ''} className="form-control form-control-sm brds" readOnly
                                  style={{ color: 'Black', backgroundColor: 'LightGrey', textAlign: 'center' }} />
                              </div>
                            </div>
                            <div className="col-md-1">
                              <div className="form-group">
                                <span style={{ textAlign: 'center' }}>CONTA</span>
                                <input type="text" value={item.conta || ''} className="form-control form-control-sm brds" readOnly
                                  style={{ color: 'Black', backgroundColor: 'LightGrey', textAlign: 'center' }} />
                              </div>
                            </div>
                            <div className="col-md-1">
                              <div className="form-group">
                                <span style={{ textAlign: 'center' }}>DV</span>
                                <input type="text" value={item.dvConta || ''} className="form-control form-control-sm brds" readOnly
                                  style={{ color: 'Black', backgroundColor: 'LightGrey', textAlign: 'center' }} />
                              </div>
                            </div>
                          </>
                        ) : (
                          <>
                            <div className="col-md-2">
                              <div className="form-group">
                                <span style={{ textAlign: 'center' }}>TIPO PAGTO</span>
                                <input type="text" value={TipoPagamentoEnum.find(t => t.value === item.tipoPagamento.toString())?.label || ''} className="form-control form-control-sm brds" readOnly
                                  style={{ color: 'Black', backgroundColor: 'LightGrey', textAlign: 'center' }} />
                              </div>
                            </div>
                            <div className="col-md-4">
                              <div className="form-group">
                                <span style={{ textAlign: 'center' }}>INFORMAÇÃO / LINHA DIGITÁVEL</span>
                                <input type="text" value={item.informacaoPagamento || ''} className="form-control form-control-sm brds" readOnly
                                  style={{ color: 'Black', backgroundColor: 'LightGrey', textAlign: 'center' }} />
                              </div>
                            </div>
                          </>
                        )}
                        <div className="col-md-1">
                          <div className="form-group">
                            <span style={{ textAlign: 'center' }}>VALOR</span>
                            <input type="text" value={formatarMoeda(item.saldoDevedor)} className="form-control form-control-sm brds" readOnly
                              style={{ color: 'Black', backgroundColor: 'LightGrey', textAlign: 'center' }} />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            ) : botaoPagina === 2 ? (
              /* ABA 2: PARCELAS */
              <div className="table-responsive">
                <div style={{ width: '100%', borderWidth: '2px', marginTop: '10px' }}>
                  <table cellSpacing="2" cellPadding="4" rules="all" style={{ border: '3', color: 'Black', borderColor: '#999999', borderWidth: '3px', borderStyle: 'Solid', width: '100%' }} className="table table-bordered table-striped">
                    <tbody>
                      <tr className="GridHeaderStyle" style={{ backgroundColor: '#white', color: 'black', textAlign: 'center' }}>
                        <th scope="col" id="App-header-table">NÚMERO</th>
                        <th scope="col" id="App-header-table">VENCIMENTO</th>
                        <th scope="col" id="App-header-table">VALOR PARCELA</th>
                        <th scope="col" id="App-header-table">TIPO</th>
                        <th scope="col" id="App-header-table">HISTORICO FIN.</th>
                        <th scope="col" id="App-header-table">MOVIMENTAÇÃO</th>
                        <th scope="col" id="App-header-table">NATUREZA BAIXA</th>
                        <th scope="col" id="App-header-table">MOVIMENTAÇÃO FIN.</th>
                        <th scope="col" id="App-header-table">VALOR PAGO</th>
                        <th scope="col" id="App-header-table">DESCONTO</th>
                        <th scope="col" id="App-header-table">SALDO REMANESCENTE</th>
                      </tr>
                      {parcelasContrato.map((item, index) => (
                        <tr className="GridRowStyle" key={item.id ?? `${item.numeroParcela}-${index}`}>
                          <td style={{ textAlign: 'center' }}>{formatarTextoDetalheParcela(item.numeroParcela)}</td>
                          <td>
                            {item.dtVencimento ? item.dtVencimento.toString().substring(0, 10).split('-').reverse().join('/') : ''}
                          </td>
                          <td style={{ textAlign: 'center' }}>
                            {formatarMoeda(item.valorParcela)}
                          </td>
                          <td style={{ textAlign: 'left' }}>
                            {formatarTipoDetalheParcela(item.tipoMovimentacao)}
                          </td>
                          <td style={{ textAlign: 'left' }}>
                            {formatarTextoDetalheParcela(item.historicoFinanceiro)}
                          </td>
                          <td style={{ textAlign: 'center' }}>{formatarDataDetalheParcela(item.dtMovimentacao)}</td>
                          <td style={{ textAlign: 'center' }}>
                            {formatarTextoDetalheParcela(formatarNaturezaBaixaParcela(item))}
                          </td>
                          <td style={{ textAlign: 'center' }}>{formatarDataDetalheParcela(item.dtMovimentacaoFinanceira)}</td>
                          <td style={{ textAlign: 'center' }}>
                            {formatarValorPagoParcela(item)}
                          </td>
                          <td style={{ textAlign: 'center' }}>
                            {formatarMoeda(calcularDescontoParcela(item))}
                          </td>
                          <td style={{ textAlign: 'center' }}>
                            {formatarMoeda(calcularSaldoRemanescenteParcela(
                              item,
                              ultimaLinhaPorParcela[String(item.numeroParcela ?? index)] === index
                            ))}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : botaoPagina === 3 ? (
              /* ABA 3: SALDO DEVEDOR */
              <div className="position-relative m-md-3">
                {loading && !baixaDto ? <Spinner loading={loading} /> : (
                  <>
                    <div className="card" style={{ marginTop: '25px' }}>
                      <div className="card-body">
                        <div className="card-header">
                          <strong>Saldo Devedor</strong>
                        </div>
                        <div className="" style={{ marginTop: '10px' }}>
                          <div className="row">
                            <div className="col-md-2" style={{ paddingLeft: '10px', paddingRight: '10px' }}>
                              <label style={{ width: '100%' }}>Data Movimento</label>
                            </div>
                          </div>
                          <div className="row" style={{ marginTop: '5px' }}>
                            <div className="col-md-2" style={{ paddingLeft: '10px', marginTop: '5px' }}>
                              <DateInput
                                required
                                className="form-control"
                                value={dtMovimento}
                                onChange={(e) => setDtMovimento(e.target.value)}
                              />
                            </div>
                            <div className="col-md-3">
                              <button className="input-group-btn" id="App-button-cor-principal"
                                style={{
                                  width: '40%', display: 'flex', alignItems: 'center',
                                  justifyContent: 'center', border: '1px solid LightGrey', borderRadius: '5px', height: '50px', minWidth: '50px'
                                }} onClick={listarParcelasDesconto}>
                                <a>
                                  <FontAwesomeIcon icon={faCalculator} style={{ color: 'White', fontSize: 'larger', marginTop: '5px' }} />
                                </a>
                                <strong style={{ color: 'White', marginLeft: '5px', fontSize: 'larger' }}> Calcular</strong>
                              </button>
                            </div>
                          </div>
                        </div>
                        {exibeDetalhe && (
                          <div className="row" style={{ marginTop: '10px' }}>
                            <div className="col-md-3">
                              <div className="form-group">
                                <span style={{ textAlign: 'left' }}>Valor Bruto</span>
                                <input type="text" value={formatarMoeda(totaisParcelasSelecionadas.valorBruto)} className="form-control form-control-sm brds " style={{ color: 'Black', backgroundColor: 'LightGrey' }} readOnly />
                              </div>
                            </div>
                            <div className="col-md-3">
                              <div className="form-group">
                                <span style={{ textAlign: 'left' }}>Desconto</span>
                                <input type="text"
                                  value={formatarMoeda(totaisParcelasSelecionadas.desconto)}
                                  className="form-control form-control-sm brds "
                                  style={{ color: 'Black', backgroundColor: 'LightGrey' }} readOnly />
                              </div>
                            </div>
                            <div className="col-md-3">
                              <div className="form-group">
                                <span style={{ textAlign: 'left' }}>Saldo Devedor</span>
                                <input type="text" value={formatarMoeda(totaisParcelasSelecionadas.saldoDevedor)} className="form-control form-control-sm brds " style={{ color: 'Black', backgroundColor: 'LightGrey' }} readOnly />
                              </div>
                            </div>
                          </div>
                        )}
                        {exibeDetalhe && (
                          parcelasList != null && parcelasList.length > 0 ? (
                            <div className="table-responsive">
                              <div style={{ width: '100%', borderWidth: '1px', marginTop: '10px' }}>
                                <table cellSpacing="2" cellPadding="4" rules="all" style={{ border: '3', color: 'Black', borderColor: '#999999', borderWidth: '3px', borderStyle: 'Solid', width: '100%' }} className="table table-bordered table-striped">
                                  <tbody>
                                    <tr className="GridHeaderStyle" style={{ backgroundColor: 'white', color: 'black' }}>
                                      <th style={{ textAlign: 'center' }} scope="col" id="App-header-table">
                                        <input
                                          type="checkbox"
                                          checked={todasParcelasSelecionadas}
                                          onChange={(e) => selecionarTodasParcelas(e.target.checked)}
                                          aria-label="Selecionar todas as parcelas"
                                        /> Tudo
                                      </th>
                                      <th style={{ textAlign: 'center' }} scope="col" id="App-header-table">NÚMERO</th>
                                      <th style={{ textAlign: 'center' }} scope="col" id="App-header-table">VENCIMENTO</th>
                                      <th style={{ textAlign: 'center' }} scope="col" id="App-header-table">STATUS</th>
                                      <th style={{ textAlign: 'center' }} scope="col" id="App-header-table">ATRASO</th>
                                      <th style={{ textAlign: 'center' }} scope="col" id="App-header-table">VALOR PARCELA</th>
                                      <th style={{ textAlign: 'center' }} scope="col" id="App-header-table">DESCONTO</th>
                                      <th style={{ textAlign: 'center' }} scope="col" id="App-header-table">SALDO</th>
                                    </tr>
                                    {parcelasList.map((item) => (
                                      <tr className="GridRowStyle" key={getParcelaQuitacaoKey(item)}>
                                        <td style={{ textAlign: 'center' }}>
                                          <input
                                            type="checkbox"
                                            checked={parcelasSelecionadas.includes(getParcelaQuitacaoKey(item))}
                                            onChange={(e) => selecionarParcela(item, e.target.checked)}
                                            aria-label={`Selecionar parcela ${item.numeroParcela || ''}`}
                                          />
                                        </td>
                                        <td style={{ textAlign: 'center' }}>{item.numeroParcela || 'N/A'}</td>
                                        <td style={{ textAlign: 'center' }}>{item.dtVencimento ? item.dtVencimento.toString().substring(0, 10).split('-').reverse().join('/') : 'N/A'}</td>
                                        <td style={{ textAlign: 'center' }}>{formatarStatusParcela(item.tipoMovimentacao)}</td>
                                        <td style={{ textAlign: 'center' }}>{item.atraso != null ? `${item.atraso}` : '0'}</td>
                                        <td style={{ textAlign: 'center' }}>{formatarMoeda(item.valorParcela)}</td>
                                        <td style={{ textAlign: 'center' }}>{formatarMoeda(item.desconto)}</td>
                                        <td style={{ textAlign: 'center' }}>
                                          {item.tipoMovimentacao !== null && item.tipoMovimentacao?.startsWith('BAIXA') && item.tipoMovimentacao !== 'BAIXA_PARCIAL'
                                            ? 'R$ 0,00'
                                            : formatarMoeda(item.saldo)
                                          }
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          ) : parcelasList != null && (
                            <div style={{ width: '100%', borderWidth: '1px', marginTop: '10px' }}>
                              <span style={{ color: 'red' }}>Não há saldo em aberto para este Contrato!</span>
                            </div>
                          )
                        )}
                      </div>
                    </div>
                  </>
                )}
              </div>
            ) : botaoPagina === 4 ? (
              cessaoOperacao ? (
                <div className="card" style={{ marginTop: '25px' }}>
                  <div className="card-body">
                    <div className="card-header">
                      <strong>FIDC</strong>
                    </div>
                    <div className="row mt-2">
                      <div className="col-md-2">
                        <div className="form-group">
                          <span>ID FIDC</span>
                          <input
                            type="text"
                            value={cessaoOperacao?.idFidc ?? 'Não Disponível'}
                            className="form-control form-control-sm brds"
                            style={{ color: 'Black', backgroundColor: 'LightGrey' }}
                            readOnly
                          />
                        </div>
                      </div>
                      <div className="col-md-4">
                        <div className="form-group">
                          <span>Nome do FIDC</span>
                          <input
                            type="text"
                            value={cessaoOperacao?.nomeFidc || 'Não Disponível'}
                            className="form-control form-control-sm brds"
                            style={{ color: 'Black', backgroundColor: 'LightGrey' }}
                            readOnly
                          />
                        </div>
                      </div>

                      <div className="col-md-3">
                        <div className="form-group">
                          <span>CNPJ do FIDC</span>
                          <input
                            type="text"
                            value={cessaoOperacao?.cnpjFidc || 'Não Disponível'}
                            className="form-control form-control-sm brds"
                            style={{ color: 'Black', backgroundColor: 'LightGrey' }}
                            readOnly
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="card-body">
                    <div className="card-header">
                      <strong>Detalhes da Cessão</strong>
                    </div>
                    <div className="row mt-2">
                      <div className="col-md-3">
                        <div className="form-group">
                          <span>Protocolo da Cessão</span>
                          <input
                            type="text"
                            value={cessaoOperacao?.protocoloCessao || 'Não Disponível'}
                            className="form-control form-control-sm brds"
                            style={{ color: 'Black', backgroundColor: 'LightGrey' }}
                            readOnly
                          />
                        </div>
                      </div>
                      <div className="col-md-3">
                        <div className="form-group">
                          <span>Valor Cedido</span>
                          <input
                            type="text"
                            value={formatarMoeda(cessaoOperacao?.valorCedido)}
                            className="form-control form-control-sm brds"
                            style={{ color: 'Black', backgroundColor: 'LightGrey', textAlign: 'right' }}
                            readOnly
                          />
                        </div>
                      </div>
                      <div className="col-md-3">
                        <div className="form-group">
                          <span>Qtd. Parcelas Cedidas</span>
                          <input
                            type="text"
                            value={cessaoOperacao?.quantidadeParcelasCedidas ?? '0'}
                            className="form-control form-control-sm brds"
                            style={{ color: 'Black', backgroundColor: 'LightGrey', textAlign: 'right' }}
                            readOnly
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="card" style={{ marginTop: '25px' }}>
                  <div className="p-3">
                    <span style={{ color: 'red' }}>Nenhum registro localizado!</span>
                  </div>
                </div>
              )
            ) : null}
            <ScrollToTopButton />
          </>
        )}
      </div>
    </>
  );
}

export default DetalheContratoComponent;
